/**
 * Detail "Set schedule…" — the outline bulk handler, run against a selection of one.
 *
 * The board's record ⌘K and its bulk ⌘K must offer one row the same actions. Rather than a second
 * implementation that drifts, this synthesises the outline row the board would have handed over and
 * calls `handleOutlineSetSchedule` with it.
 */
import { computed, inject } from '@angular/core';
import { Entity } from '@jms/model';
import { ScheduleWeekday } from '@jms/model/scheduling/schedule.rule';
import { EntityDataService, EntityDataServiceRaw } from '@workbuddy/services/entities';
import { ToastService } from '@workbuddy/ui/services';
import { WorkBuddyDialogService } from '@workbuddy/ui/v2/blocks/dialog';
import { BOARD_PANEL_HOST } from '@workbuddy/ui/v2/blocks/entity.board/components/board.panel/board.panel.host';
import { EntityBoardStore } from '@workbuddy/ui/v2/blocks/entity.board/state';
import type { ActionCommandCapability, ActionCommandContext, ActionCommandDescriptor, SurfaceContextLike } from '@workbuddy/ui/v2/surface.engine';
import { handleOutlineSetSchedule, OutlineRowPatcher, OUTLINE_SET_SCHEDULE_ACTION } from '../bulk/outline.bulk.schedule';
import { NOOP_OUTLINE_ROW_PATCHER, reloadSurfaceRecord } from '../bulk/outline.record.command';
import { outlineRowForTask, OutlineRecordTask, singleOutlineCommandArgs } from '../bulk/outline.record';
import { OUTLINE_SET_SCHEDULE_LABEL } from '../relation/outline.relation.builder';
import { OutlineTaskCommandConfig } from './outline.detail.command.config';

/** The calendar the dialog asks its questions against — the same one the schedule card edits with. */
export interface OutlineScheduleCalendar {
  today?: string;
  workWeek?: ScheduleWeekday[];
  timezone?: string;
}

export function createOutlineSetScheduleCommand<TTask extends OutlineRecordTask & Entity>(config: OutlineTaskCommandConfig<TTask>, $calendar: () => OutlineScheduleCalendar | null, fallbackToday: () => string): ActionCommandCapability<TTask> {

  const dialogs = inject(WorkBuddyDialogService);
  const entityData = inject(EntityDataService);
  const entityDataRaw = inject(EntityDataServiceRaw);
  const toasts = inject(ToastService);
  const board = inject(EntityBoardStore, { optional: true });
  const panel = inject(BOARD_PANEL_HOST, { optional: true });

  return {
    descriptor: ({ page }: ActionCommandContext<TTask>) => computed<ActionCommandDescriptor>(() => ({
      id: OUTLINE_SET_SCHEDULE_ACTION,
      label: OUTLINE_SET_SCHEDULE_LABEL,
      iconLeft: 'fa-regular fa-calendar',
      // A subtask rides its parent's cadence and has no schedule of its own to set.
      hidden: !page.$model()?._id || page.$readonly() || isSubtaskModel(config, page.$model()),
      disabled: page.$saving(),
      pin: 'never',
    })),
    run: async (page: SurfaceContextLike<TTask>) => {

      const record = page.$model();
      const row = record && !page.$readonly() && !config.isSubtask(record) ? outlineRowForTask(record, config.kinds, config) : null;
      if (!row || !record) return;

      const calendar = $calendar();
      const store: OutlineRowPatcher = board ?? NOOP_OUTLINE_ROW_PATCHER;
      const outcome = await handleOutlineSetSchedule(singleOutlineCommandArgs(OUTLINE_SET_SCHEDULE_ACTION, row), {
        kinds: config.kinds,
        dialogs,
        entityData,
        store,
        toasts,
        today: calendar?.today ?? fallbackToday(),
        workWeek: calendar?.workWeek ?? [],
        timezone: calendar?.timezone,
      });
      // `clearSelection: false` is how the handler says the dialog was dismissed or nothing applied.
      if (outcome.clearSelection === false) return;
      await reloadSurfaceRecord(entityDataRaw, config.entity, page, record._id, config.toModel);
      if (!board) panel?.refresh('collection');
    },
  };
}

/** Null-safe subtask probe for the descriptor, which runs before the model has loaded. */
function isSubtaskModel<TTask extends OutlineRecordTask>(config: OutlineTaskCommandConfig<TTask>, model: TTask | null | undefined): boolean {

  return !!model && config.isSubtask(model);
}
